import React, { useState } from "react";
import { trackEvent } from "@leafly-com/web-utils";
import cx from "classnames";
import getConfig from "next/config";
import { useSelector } from "react-redux";

import getDealsCount from "api/requests/getDealsCount";
import useOnComponentInView from "hooks/useOnComponentInView";
import useSplit from "hooks/useSplit";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCoordinates, getFormattedLocation } from "redux/selectors/location";

import Button from "components/botanic/Button";
import Logo from "components/Icons/logo.svg";
import Image from "components/Image";
import Placeholder, { Rect } from "components/Placeholder";
import TrackImpression from "components/TrackImpression";

import DealsTakeoverNoResults from "./DealsTakeoverNoResults";

const {
  publicRuntimeConfig: {
    imgix: { publicUrl },
  },
} = getConfig();

type DealsTakeoverProps = {
  className?: string;
  ctaLabel?: string; // optional label for the CTA button - defaults to "explore deals"
  gaCategory: string; // the Category to track in Google Analytics for impression/click
  gaLabel?: string /* the Label to track in Google Analytics for impression/click
                      default value is based on the current badge (eg "100+ deals") */;
  splitClickEvent?: string /* the event to track in Split for clicking deals
                              if you leave blank, Split click tracking will not happen */;
  splitSignupEvent?: string /* the event to track in Split for email signup
                               if you leave blank, Split signup tracking will not happen */;
  signupSource: string; // the 'source' field if  user signs up for email from this component
  handleClick?: () => void; // optional callback to handle click on the deals button, otherwise button will navigate to deals page
  hideLocation?: boolean; // if true, hide the "in Tucson, AZ" text/button
};
const DealsTakeover = ({
  className,
  ctaLabel,
  gaCategory,
  gaLabel,
  splitClickEvent,
  splitSignupEvent,
  signupSource,
  handleClick,
  hideLocation = false,
}: DealsTakeoverProps) => {
  const [dealsCount, setDealsCount] = useState<number>(0);
  const formattedLocation = useSelector(getFormattedLocation);
  const coordinates = useSelector(getCoordinates);
  const countryCode = useSelector(getDomainCountryCode);

  const {
    dispatch: { track },
  } = useSplit();

  const getDealsCountData = async () => {
    const dealsCountData = await getDealsCount(coordinates, countryCode);
    setDealsCount(dealsCountData.count);
  };

  const [ref] = useOnComponentInView(getDealsCountData, { triggerOnce: true });

  const getBadgeCount = (count: number) => {
    if (!count || count === 0) {
      return 0;
    } else if (count > 0 && count < 10) {
      return count;
    } else if (count >= 10 && count < 25) {
      return "10+";
    } else if (count >= 25 && count < 50) {
      return "25+";
    } else if (count >= 50 && count < 100) {
      return "50+";
    } else if (count >= 100 && count < 200) {
      return "100+";
    } else if (count >= 200 && count < 500) {
      return "200+";
    } else if (count >= 500) {
      return "500+";
    }
  };

  const getAnalyticsCountLabel = () =>
    `${getBadgeCount(dealsCount)} ${dealsCount === 1 ? "deal" : "deals"}`;

  const getDealsBadgeLabel = () => {
    return dealsCount === 0
      ? "Deals"
      : `${getBadgeCount(dealsCount)} ${dealsCount === 1 ? "deal" : "deals"}`;
  };

  const trackAnalyticsClick = () => {
    const label = gaLabel || getAnalyticsCountLabel();
    trackEvent(gaCategory, "Click", label);
    splitClickEvent && track(splitClickEvent);
  };

  return (
    <div ref={ref}>
      <style jsx>{`
        .deals-takeover-wrapper {
          height: 549px;
        }

        .isolate {
          isolation: isolate;
        }

        .deals-takeover-cta {
          max-width: 380px;
          position: absolute;
          bottom: 32px;
          right: 0px;
          left: 0px;
          margin-left: auto;
          margin-right: auto;
        }

        .deals-exclusive-logo {
          padding-bottom: 1.5px;
          margin-left: 1px;
        }

        @media (min-width: 768px) {
          .deals-takeover-wrapper {
            height: 375px;
          }

          .deals-takeover-cta {
            max-width: 343px;
            position: absolute;
            top: 94px;
            margin-left: 0px;
          }
        }

        @media (min-width: 1024px) {
          .deals-takeover-cta {
            margin-left: 40px;
          }
        }
      `}</style>
      <div className={cx("relative deals-takeover-wrapper", className)}>
        <Image
          alt="Deals and promotions"
          background={true}
          mobileSrc={`${publicUrl}/web-home/deals-v2-mobile.png`}
          sizes={[544, 767, 1023, 1440, null, 767]}
          src={`${publicUrl}/web-home/deals-v2-desktop.png`}
          tabletSrc={`${publicUrl}/web-home/deals-v2-desktop.png`}
        />
        <div className="container md:relative">
          <div className="deals-takeover-cta isolate px-lg text-md">
            {dealsCount !== undefined ? (
              <TrackImpression
                category={gaCategory}
                label={gaLabel || getAnalyticsCountLabel()}
              >
                <div
                  className="inline-block font-bold text-white text-sm bg-notification px-sm rounded"
                  data-testid="deals-takeover-badge"
                >
                  {getDealsBadgeLabel()}
                </div>
                {dealsCount === 0 ? (
                  <DealsTakeoverNoResults
                    gaCategory={gaCategory}
                    splitSignupEvent={splitSignupEvent}
                    signupSource={signupSource}
                  />
                ) : (
                  <div className="mt-xs" data-testid="deals-takeover-results">
                    <span
                      className="font-extrabold"
                      data-testid="deals-takeover-count"
                    >
                      {dealsCount}{" "}
                      {dealsCount === 1 ? "deal & promo " : "deals & promos "}
                    </span>
                    {formattedLocation && !hideLocation ? (
                      <>
                        just for you in{" "}
                        <Button
                          href={!handleClick ? "/deals" : ""}
                          variant="text"
                          className="inline-block text-green underline text-left text-md"
                          aria-label="Go to Deals"
                          onClick={
                            handleClick ? handleClick : trackAnalyticsClick
                          }
                          data-testid="deals-takover-location-button"
                        >
                          {formattedLocation}
                        </Button>
                      </>
                    ) : (
                      " near you"
                    )}
                    <Button
                      href={!handleClick ? "/deals" : ""}
                      className="text-sm mt-lg"
                      width="full"
                      style={{ height: 48, paddingTop: 14 }}
                      data-testid="deals-takover-cta-button"
                      onClick={handleClick ? handleClick : trackAnalyticsClick}
                    >
                      {ctaLabel ? ctaLabel : `explore deals`}
                    </Button>
                    <div className="text-xs mt-md text-center md:text-left">
                      exclusively on{" "}
                      <Logo
                        className="deals-exclusive-logo inline flex-shrink-0 text-green"
                        width="38"
                        height="38"
                      />
                    </div>
                  </div>
                )}
              </TrackImpression>
            ) : (
              <>
                <Placeholder width="100%" height="32" className="">
                  <Rect width="100%" height="100%" />
                </Placeholder>
                <Placeholder width="100%" height="18" className="mt-sm">
                  <Rect width="100%" height="100%" />
                </Placeholder>
                <Placeholder
                  width="100%"
                  height="50"
                  className="mt-lg rounded-full"
                >
                  <Rect width="100%" height="100%" />
                </Placeholder>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealsTakeover;
